/**
 * useWatchlist.js - Hook for managing watchlist data
 *
 * Provides watchlist data and automatically refreshes when profiles change
 */
import { useState, useEffect, useCallback } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { userDataService } from '../services/UserDataService';
import { useAuth0 } from '@auth0/auth0-react';

export const useWatchlist = () => {
  const { currentProfile } = useProfile();
  const { isAuthenticated, user } = useAuth0();
  const [watchlistData, setWatchlistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingItems, setRemovingItems] = useState(new Set()); // Track items being removed
  const [error, setError] = useState(null);

  const refreshWatchlist = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch fresh watchlist data from API and update internal cache
      const freshWatchlistData = await userDataService.refreshWatchlistData();

      setWatchlistData(freshWatchlistData || []);
    } catch (err) {
      console.error('useWatchlist: Error refreshing watchlist data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentProfile]);

  // Refresh watchlist when profile changes
  useEffect(() => {
    if (currentProfile?._id && isAuthenticated && user) {
      refreshWatchlist();
    } else {
      // Clear watchlist data when no profile is selected or user not authenticated
      setWatchlistData([]);
      setLoading(false);
      setError(null);
    }
  }, [currentProfile?._id, isAuthenticated, user, refreshWatchlist]);

  // Helper methods that use the current watchlist data
  const isInWatchlist = useCallback((channelId, parentChannelId = null) => {
    return userDataService.isInWatchlist(channelId, parentChannelId);
  }, []);

  const getWatchlistCount = useCallback(() => {
    return userDataService.getWatchlistCount();
  }, []);

  const addToWatchlist = useCallback(
    async (channelId, parentChannelId = null) => {
      try {
        const result = await userDataService.addToWatchlist(channelId, parentChannelId);
        if (result.success) {
          // Refresh the watchlist data after successful add
          await refreshWatchlist();
        }
        return result;
      } catch (error) {
        console.error('useWatchlist: Error adding to watchlist:', error);
        throw error;
      }
    },
    [refreshWatchlist],
  );

  const removeFromWatchlist = useCallback(
    async channelId => {
      try {
        // Add item to removing set
        setRemovingItems(prev => new Set([...prev, channelId]));
        
        // Optimistic UI update - remove item immediately from local state
        setWatchlistData(prevData => prevData.filter(item => item._id !== channelId));
        
        const result = await userDataService.removeFromWatchlist(channelId);
        if (!result.success) {
          // If API call failed, refresh to restore the item
          await refreshWatchlist();
        }
        return result;
      } catch (error) {
        console.error('useWatchlist: Error removing from watchlist:', error);
        // If error occurred, refresh to restore the item
        await refreshWatchlist();
        throw error;
      } finally {
        // Remove item from removing set
        setRemovingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(channelId);
          return newSet;
        });
      }
    },
    [refreshWatchlist],
  );

  // Helper function to check if an item is being removed
  const isRemoving = useCallback((channelId) => {
    return removingItems.has(channelId);
  }, [removingItems]);

  return {
    watchlistData,
    loading,
    removingItems,
    isRemoving,
    error,
    refreshWatchlist,
    isInWatchlist,
    getWatchlistCount,
    addToWatchlist,
    removeFromWatchlist,
    hasWatchlist: watchlistData.length > 0,
  };
};
